import isEmail from 'validator/lib/isEmail';
import isUrl from 'validator/lib/isURL';
import { actionChannel, call, delay, fork, put, select, take, takeEvery, takeLatest, takeLeading } from 'redux-saga/effects';
import {
    AccessLevel,
    AccountCore,
    AccountOptions,
    AdvancedOptions,
    CrcFormPayload,
    CRCStatus,
    ProductCode,
} from '@dev/ee-api/ee-api-models';
import { filter, identity, isEmpty, pickBy, includes } from 'lodash-es';
import store, { local } from 'store2';
import { stringify } from 'query-string';
import { sendGoogleAddTags, setPublicDataForGtm } from '@dev/ee-tools';

import Authentication from '@api/services/Authentication';
import {
    createAccount,
    deleteAccount,
    deleteFile,
    getAccountsName,
    getAccountWarnings,
    getCustomBranding,
    getDisposableToken,
    loadAccount,
    purchasePrivateIP,
    resetPassword as resetPasswordCall,
    sendCrcFormParams,
    updateAdvancedOptions,
    updateGlobalContacts,
    updateMarketingOptions,
    validateCRCForm,
    login,
    purchaseExtendedLogsPOST,
    setValidSenderDomainOnly,
    resetAccountEmailPUT,
    googleTagsGET,
    googleTagsPOST,
} from '@api/services/ElasticApi';
import {
    AccountEditMarketingOptionsSuccess,
    AccountErrorUpdate,
    AccountGetCustomBrandingError,
    AccountGetCustomBrandingSuccess,
    AccountLoadingError,
    AccountLoadingSuccess,
    AccountStartLoading,
    AccountStartUpdate,
    AccountSuccessUpdate,
    AccountUploadLogoFinish,
    CrcFormFailure,
    CrcFormSuccess,
    CrcValidateFailure,
    CrcValidateSuccess,
    CreateAccountError,
    CreateAccountStart,
    CreateAccountSuccess,
    CreateAccountWithGoogleSuccess,
    GetAccountsNameError,
    GetAccountsNameSuccess,
    getAccountStore,
    GetAccountWarningsSuccess,
    ResetPasswordError,
    ResetPasswordStart,
    ResetPasswordSuccess,
    ResetPasswordUser,
    SetValidSenderDomainsError,
    UpdateCrcStatusInAccount,
    UpdateGeneralInfo,
    ValidateInputError,
    ValidateInputSuccess,
    ResetAccountEmailSuccess,
    ResetAccountEmailError,
} from '@api/store/account/actions';
import {
    AccountActionTypes,
    AccountDeleteLogoAction,
    AccountState,
    AccountUpdateGlobalContactsAction,
    AccountUpdateStartAction,
    AccountUploadLogoAction,
    CrcValidateAction,
    CreateAccountAction,
    CreateAccountWithGoogleAction,
    DeleteAccountAction,
    GetAccountsNameAction,
    getCRCParams,
    LoginWithDisposbaleTokenAction,
    PurchasePrivateIPAction,
    ResetPasswordAction,
    ResetPasswordUserAction,
    SendCrcFormAction,
    SetValidSenderDomainsAction,
    ValidateInputAction,
    ResetAccountEmailAction,
} from '@api/store/account/types';
import { ShowToast } from '@api/store/toast/actions';
import { UploadFiles } from '@api/store/upload/actions';
import { UserFilesState } from '@api/store/userfiles/types';
import { BillingPaths, HomePaths, pathsParams } from '@api/services/dictionaries/paths';
import { ApplicationState as State } from '@api/store';
import { getApiPath } from '@api/services/Security';
import { StorageKeysEnum } from '@api/services/dictionaries/StorageKeys';
import { URLSearchParamsEnum } from '@api/services/dictionaries/UrlSearchParams';

import i18n from '@api/i18n';
import { resetUserPassword } from '@api/services/ApiCategories/users';
import { ConfigState, getConfigStore } from '@api/store/config/types';
import { GetAccountNotificatins } from '@api/store/notifications/actions';
import { LoadProductList } from '@api/store/products/actions';
import { errorToast } from './commonGenerators/errorToast';
import { SessionSuccessAuth } from '@api/store/session/actions';
import { push } from 'redux-first-history';
import { LoadAllIPs } from '@api/store/pools/actions';
import { GetDashboardSeenNotifications } from '@api/store/dashboardSeenNotifications/actions';
import { Logger } from '@api/services/errors/appLogger';

const getUserFiles = (state: State): UserFilesState => state.userFiles;
const getGeneralInfo = (state: State): AccountCore => state.account.GeneralInfo;

function* fetchAccount(): any {
    try {
        const account = yield call(loadAccount);
        const { AccountAccessLevel }: AccountCore = account?.data?.GeneralInfo || {};

        setPublicDataForGtm(account?.data);
        yield put(AccountLoadingSuccess(account.data));
        yield put(LoadProductList());

        if (AccountAccessLevel?.includes(AccessLevel.ViewAccount)) {
            yield put(GetAccountNotificatins());
        }
        yield put(GetDashboardSeenNotifications());
    } catch (e) {
        yield put(AccountLoadingError());
        yield call(errorToast, e);
    }
}

function* sendGoogleAdTags(): any {
    try {
        const googleAdTags = yield call(googleTagsGET);
        if (isEmpty(googleAdTags)) {
            return;
        }

        sendGoogleAddTags(googleAdTags);

        yield call(googleTagsPOST, { GoogleTags: googleAdTags });
    } catch (e) {
        const error = e?.response?.data?.Error ? e.response.data.Error : e.message;
        Logger.error(error);
    }
}

function* startUpdate(action: AccountUpdateStartAction): any {
    const { payload, meta } = action;
    const currentAccount = yield select((state): any => state.account.AdvancedOptions);
    const updatedAdvancedOptions: AdvancedOptions = {
        ...currentAccount,
        [payload.option]: payload.value,
    };
    if (!updatedAdvancedOptions.EmailNotificationForError) {
        updatedAdvancedOptions.EmailNotification = '';
    }

    try {
        const update = yield call(updateAdvancedOptions, updatedAdvancedOptions);
        const isOptionUpdated = payload.value === update.data[payload.option];
        if (!isOptionUpdated) {
            throw new Error('Option was not updated. Try to refresh the page.');
        }
        yield put(AccountSuccessUpdate({ AdvancedOptions: update.data }, meta));
        yield put(
            ShowToast({
                message: i18n.t('settingsupdated'),
                severity: 'success',
                duration: 500,
            })
        );
    } catch (e) {
        yield put(AccountErrorUpdate({ [payload.option]: currentAccount[payload.option] }, meta));
        yield call(errorToast, e);
    }
}

function* updateAccountChannel(): any {
    const openedChannel = yield actionChannel(AccountActionTypes.UPDATE_START);
    while (true) {
        const action = yield take(openedChannel);
        yield call(startUpdate, action);
    }
}

function* updateDeliveryOptimizationEngine(action: AccountUpdateGlobalContactsAction): any {
    const {
        payload: { globalcontacts },
        meta,
    } = action;

    try {
        yield call(updateGlobalContacts, globalcontacts);
        const currentGeneralInfo = yield select((state): AccountCore => state.account.GeneralInfo);
        const updatedGeneralInfo = {
            ...currentGeneralInfo,
            EnableGlobalContacts: globalcontacts,
        };
        yield put(AccountSuccessUpdate({ GeneralInfo: updatedGeneralInfo }, meta));
        yield put(
            ShowToast({
                message: i18n.t('settingsupdated'),
                severity: 'success',
                duration: 500,
            })
        );
    } catch (e) {
        yield call(errorToast, e);
    }
}

function* uploadLogo(action: AccountUploadLogoAction): any {
    const { payload } = action;
    try {
        const uploaded = yield put(UploadFiles(payload.file));
        yield take('@@userFiles/LOADING_FILES_SUCCESS');
        const uploadedFiles = yield select(getUserFiles);
        const generalInfo: AccountCore = yield select(getGeneralInfo);
        const config = yield select(getConfigStore);
        const allFiles = uploadedFiles?.files;
        const logoFile = allFiles?.find((el): any => el?.FileName === uploaded?.payload?.file?.name);
        const privatBrandingUrlWithProtocol = generalInfo?.PrivateBrandingUrl?.startsWith('http')
            ? generalInfo?.PrivateBrandingUrl
            : `https://${generalInfo.PrivateBrandingUrl}`;
        const domain = generalInfo.PrivateBrandingUrl ? privatBrandingUrlWithProtocol : getApiPath(config);
        yield put(AccountStartUpdate('LogoUrl', `${domain}/userfile/${logoFile?.MediumScreenshotPath}`));
        yield take(AccountActionTypes.UPDATE_SUCCESS);
        yield put(AccountUploadLogoFinish());
    } catch (e) {
        yield put(AccountUploadLogoFinish());
        yield call(errorToast, e);
    }
}

function* deleteLogo(action: AccountDeleteLogoAction): any {
    const { payload } = action;
    try {
        yield put(AccountStartUpdate('LogoUrl', ''));
        const fileToDelete = yield call(deleteFile, decodeURIComponent(payload.fileName)); // eslint-disable-line
    } catch (e) {
        yield call(errorToast, e);
    }
}

function* newAccountRedirectPath(): any {
    const { bootedAsChild }: ConfigState = yield select(getConfigStore);
    const needToBePushedToBilling = store.local.get(StorageKeysEnum.Tier) || store.session.get(StorageKeysEnum.Tier);

    if (needToBePushedToBilling && bootedAsChild) {
        window.location.assign(BillingPaths.BillingPlanList.replace(pathsParams.productCode, ProductCode.EmailApi.toLowerCase()));
        return;
    }

    yield put(push(HomePaths.Home));
}

function* createNewAccount(action: CreateAccountAction | CreateAccountWithGoogleAction): any {
    const { payload, meta, type } = action;
    const isGoogleRegistration = type === AccountActionTypes.CREATE_ACCOUNT_WITH_GOOGLE;

    yield put(CreateAccountStart());

    try {
        const apiCall = isGoogleRegistration ? login : createAccount;
        const resp = yield call(apiCall, payload);

        const account = resp?.data;
        const browserToken = !isGoogleRegistration && resp?.headers['x-elasticemail-browsertoken'];
        const accessToken = isGoogleRegistration ? resp?.ApiKey : resp?.headers['x-elasticemail-accesstoken'];

        if (browserToken && local.get('browserToken') !== browserToken) {
            local.set('browserToken', browserToken);
        }

        Authentication.setAccessToken(accessToken);
        yield put(SessionSuccessAuth());

        if (isGoogleRegistration) {
            yield put(CreateAccountWithGoogleSuccess(meta));
        } else {
            yield put(CreateAccountSuccess(account, meta));
        }

        yield call(newAccountRedirectPath);
    } catch (e) {
        const error = e?.response?.data?.Error ? e.response.data.Error : e.message;
        Logger.error(error);
        yield put(CreateAccountError(e?.response?.data, meta));
    }
}

function* resetPassword(action: ResetPasswordAction | ResetPasswordUserAction): any {
    const { payload, meta, type } = action;
    const { email } = payload;
    const metaToPass = type === AccountActionTypes.RESET_PASSWORD_USER ? action?.previousMeta : meta;

    try {
        yield put(ResetPasswordStart());

        const apiCall = action.type === AccountActionTypes.RESET_PASSWORD ? resetPasswordCall : resetUserPassword;
        const port = location.port ? `:${location.port}` : '';
        const { routerBasePath }: ConfigState = yield select(getConfigStore);
        const productPath = routerBasePath || '';
        yield call(apiCall, `${location.protocol}//${location.hostname}${port}${productPath}/reset-password`, email);

        yield put(ResetPasswordSuccess(metaToPass));
    } catch (e) {
        if (e?.response?.data?.MessageID === 'login_accountnotfound') {
            yield put(ResetPasswordUser(email, meta));
        } else {
            const error = e?.response?.data?.Error ? e.response.data.Error : e.message;
            Logger.error(error);
            yield put(ResetPasswordError(e.response.data, metaToPass));
        }
    }
}

function* validateInput(action: ValidateInputAction): any {
    const { payload } = action;
    const { type, option } = payload;
    yield delay(600);

    const optionValue = Object.values(option)[0] as string;
    const optionName = Object.keys(option)[0];
    const split = optionValue.split(';');
    const inputErrors = split.filter((el): any => {
        if (type === 'email') {
            return !isEmail(el.trim());
        }
        if (type === 'url') {
            return !isUrl(el.trim());
        }

        return false;
    });
    if (isEmpty(inputErrors) || (inputErrors[0] === '' && inputErrors.length === 1)) {
        yield put(ValidateInputSuccess());
        return yield put(AccountStartUpdate(optionName as keyof AdvancedOptions, optionValue));
    }
    yield put(ValidateInputError(optionName));
}

function* switchProduct(action: LoginWithDisposbaleTokenAction): any {
    const {
        payload: { productCode, isBilling, dashboardUrl, accountDashPathName, goToLegacyDash },
    } = action;

    try {
        const resp = yield call(getDisposableToken);
        //TODO: change for proper domain handling now its simple check if its elasticemail
        const isEEdomain = location.origin?.includes('elasticemail');
        const accountDashDomainToRedirect = isEEdomain ? ACCOUNTDASHBOARD : location.origin;
        const { Token } = resp.data;

        if (!!accountDashPathName) {
            const productCodeForUpselling = local.get(StorageKeysEnum?.ShowProductUpselling);
            const additionalParams = {
                [URLSearchParamsEnum.ProductUpselling]: productCodeForUpselling,
                t: Token,
            };

            const urlParams = stringify(pickBy(additionalParams, identity));

            return window.location.replace(`${accountDashDomainToRedirect}${accountDashPathName}?${urlParams}`);
        }

        if (goToLegacyDash) {
            const domainToRedirect = isEEdomain ? `${LEGACYEEDASHBOARD}login` : `${location.origin}/legacy/`;
            Authentication.removeAccessToken(true);
            return window.location.replace(`${domainToRedirect}?t=${Token}`);
        }

        if (dashboardUrl) {
            return window.location.replace(`${accountDashDomainToRedirect}${dashboardUrl}/login?t=${Token}`);
        } else if (productCode && isBilling) {
            const billingPath = BillingPaths.BillingPlanList.replace(':productCode', productCode.toLowerCase());
            return window.location.replace(`${accountDashDomainToRedirect}${billingPath}?t=${Token}`);
        }
        window.location.replace(`${accountDashDomainToRedirect}/login?t=${Token}`);
    } catch (e) {
        yield call(errorToast, e);
    }
}

function* purchaseIP(action: PurchasePrivateIPAction): any {
    const { payload } = action;
    //based on v2 call, and old dashboard handling for purchasing IP
    const region = payload.region === 'any' ? '' : payload.region;

    try {
        yield call(purchasePrivateIP, region, payload.quantity);
        yield put(LoadAllIPs());
        yield put(
            ShowToast({
                message: i18n.t('privateiphasbeenaddedtoyouraccount'),
                severity: 'success',
                duration: 14000,
            })
        );
    } catch (e) {
        yield call(errorToast, e);
    }
}

function* getAccountName(action: GetAccountsNameAction): any {
    const { payload } = action;

    try {
        const accountname = yield call(getAccountsName, payload.subaccountEmail);
        yield put(GetAccountsNameSuccess(accountname.data));
    } catch (e) {
        Logger.error(e);
        yield put(GetAccountsNameError());
    }
}

function* editMarketingOptions(action): any {
    try {
        yield call(updateMarketingOptions, action.payload.options);
        const account = yield call(loadAccount);
        yield put(AccountEditMarketingOptionsSuccess(account));
    } catch (e) {
        Logger.error(e);
    }
}

function* sendCrc(action: SendCrcFormAction): any {
    const { meta } = action;
    const crcParams: CrcFormPayload = yield select(getCRCParams);

    try {
        const response = yield call(sendCrcFormParams, crcParams);

        if (!response.data) {
            throw new Error('wrong anwsers for the crc forms');
        }
        yield put(UpdateCrcStatusInAccount(CRCStatus.InProgress));
        yield put(CrcFormSuccess(meta));
    } catch (e) {
        yield put(UpdateCrcStatusInAccount(CRCStatus.NotPassed));
        yield put(CrcFormFailure(meta));
    }
}

function* crcValidate(action: CrcValidateAction): any {
    const { payload, meta } = action;
    try {
        yield call(validateCRCForm, payload.crcDecision, payload.subaccountEmail);
        yield put(UpdateCrcStatusInAccount(CRCStatus.FilledOnceOrMore));
        yield put(AccountStartLoading(false));
        yield put(CrcValidateSuccess(meta));
    } catch (e) {
        yield call(errorToast, e);
        yield put(CrcValidateFailure(meta));
    }
}

function* resetAccountEmail({ payload, meta }: ResetAccountEmailAction): any {
    try {
        yield call(resetAccountEmailPUT, { VerificationKey: payload.verificationKey, NewEmail: payload.newEmail });
        yield put(ResetAccountEmailSuccess(meta));
    } catch (e) {
        yield put(ResetAccountEmailError(meta));
        Logger.error(e);
        yield call(errorToast, e);
    }
}

function* watchResetAccountEmail(): any {
    yield takeLatest(AccountActionTypes.RESET_ACCOUNT_EMAIL_START, resetAccountEmail);
}

function* tryToDeleteAccount(action: DeleteAccountAction): any {
    const { payload } = action;
    try {
        yield call(deleteAccount, payload.password);
        Authentication.removeAccessToken();
        // yield put(push(HomePaths.AccountDeleted));
    } catch (e) {
        yield call(errorToast, e);
    }
}

function* getWarningsArray(): any {
    try {
        const resp = yield call(getAccountWarnings);

        yield put(GetAccountWarningsSuccess(resp.data));
    } catch (e) {
        yield call(errorToast, e);
    }
}

function* getCustomBrandingSaga(): any {
    if (includes(window.location.host, 'elasticemail.com')) {
        yield put(AccountGetCustomBrandingSuccess(null));
        return;
    }

    try {
        const brandingPayload = {
            Domain: location?.origin,
        };
        const brandingInfo = yield call(getCustomBranding, brandingPayload);
        yield put(AccountGetCustomBrandingSuccess(brandingInfo.data));
    } catch {
        yield put(AccountGetCustomBrandingError());
    }
}

function* enableExtendedLogs(): any {
    try {
        yield call(purchaseExtendedLogsPOST);
        yield put(AccountStartLoading());
        yield put(
            ShowToast({
                message: i18n.t('extendedlogshasbeenaddedtoaccount'),
                severity: 'success',
                duration: 1500,
            })
        );
    } catch (e) {
        const error = e?.response?.data?.Error ? e.response.data.Error : e.message;
        Logger.error(error);

        yield put(push(HomePaths.SERVICE_UNAVAILABLE));
        yield put(
            ShowToast({
                message: `Error: ${error}`,
                severity: 'error',
                duration: 14000,
            })
        );
    }
}

function* toggleValidSenderDomains({ payload, meta }: SetValidSenderDomainsAction): any {
    try {
        const { GeneralInfo: currentGenInfo }: AccountState = yield select(getAccountStore);
        const initOptions = filter(currentGenInfo.Options, (option) => option !== AccountOptions.ValidSenderDomainsOnly);
        yield call(setValidSenderDomainOnly, payload.ValidSenderDomainsOnly);

        const updatedGeneralInfo: AccountCore = {
            ...currentGenInfo,
            Options: [...initOptions, ...(payload.ValidSenderDomainsOnly ? [AccountOptions.ValidSenderDomainsOnly] : [])],
        };

        yield put(UpdateGeneralInfo(updatedGeneralInfo));
        yield put(
            ShowToast({
                message: i18n.t('settingsupdated'),
                severity: 'success',
                duration: 500,
            })
        );
    } catch (e) {
        yield put(SetValidSenderDomainsError(meta));
        yield call(errorToast, e);
    }
}

// watchers

function* watchHandleValidSenderDomainsToggle(): any {
    yield takeEvery(AccountActionTypes.SET_VALID_SENDER_DOMAINS_ONLY, toggleValidSenderDomains);
}

function* watchUpdateMarketingOptions(): any {
    yield takeLatest(AccountActionTypes.UPDATE_MARKETING_OPTIONS_START, editMarketingOptions);
}

function* watchResetPassword(): any {
    yield takeLeading([AccountActionTypes.RESET_PASSWORD, AccountActionTypes.RESET_PASSWORD_USER], resetPassword);
}

function* watchFetchAccountRequest(): any {
    yield takeLeading(AccountActionTypes.FETCH_START, fetchAccount);
}

function* watchForUploadLogoRequest(): any {
    yield takeEvery(AccountActionTypes.UPLOAD_LOGO_START, uploadLogo);
}
function* watchForDeleteLogo(): any {
    yield takeLeading(AccountActionTypes.DELETE_LOGO, deleteLogo);
}

function* watchForCreateAccount(): any {
    yield takeLeading([AccountActionTypes.CREATE_ACCOUNT, AccountActionTypes.CREATE_ACCOUNT_WITH_GOOGLE], createNewAccount);
}
function* watchForInputChange(): any {
    yield takeLatest(AccountActionTypes.VALIDATE_INPUT, validateInput);
}

function* updateDeliveryOptimizationEngineWatcher(): any {
    yield takeEvery(AccountActionTypes.UPDATE_GLOBAL_CONTACTS, updateDeliveryOptimizationEngine);
}
function* watchForLoginWithDisposableToken(): any {
    yield takeEvery(AccountActionTypes.LOGIN_WITH_DISPOSABLE_TOKEN, switchProduct);
}

function* purchasePrivateIPWatcher(): any {
    yield takeEvery(AccountActionTypes.PURCHASE_PRIVATE_IP, purchaseIP);
}

function* sendCrcFormWatcher(): any {
    yield takeEvery(AccountActionTypes.SEND_CRC, sendCrc);
}
function* getAccountsNameWatcher(): any {
    yield takeLeading(AccountActionTypes.GET_ACCOUNTS_NAME, getAccountName);
}

function* validateCrcFormWatcher(): any {
    yield takeLatest(AccountActionTypes.CRC_VALIDATION, crcValidate);
}

function* tryToDeleteAccountWatcher(): any {
    yield takeLatest(AccountActionTypes.DELETE_ACCOUNT, tryToDeleteAccount);
}
function* accountWarningsWatcher(): any {
    yield takeLatest(AccountActionTypes.GET_ACCOUNT_WARNINGS, getWarningsArray);
}

function* watchGetCustomBrandingRequest(): any {
    yield takeLeading(AccountActionTypes.GET_CUSTOM_BRANDING_START, getCustomBrandingSaga);
}

function* watchPurchaseExtendedLogsRequest(): any {
    yield takeLeading(AccountActionTypes.PURCHASE_EXTENDED_LOGS, enableExtendedLogs);
}

function* watchLoadAccountSuccess(): any {
    yield takeLatest('@@account/FETCH_SUCCESS', sendGoogleAdTags);
}

const accountSagas = [
    fork(watchFetchAccountRequest),
    fork(watchForUploadLogoRequest),
    fork(watchForDeleteLogo),
    fork(watchForInputChange),
    fork(updateDeliveryOptimizationEngineWatcher),
    fork(updateAccountChannel),
    fork(watchForCreateAccount),
    fork(watchResetPassword),
    fork(watchForLoginWithDisposableToken),
    fork(purchasePrivateIPWatcher),
    fork(getAccountsNameWatcher),
    fork(watchUpdateMarketingOptions),
    fork(sendCrcFormWatcher),
    fork(validateCrcFormWatcher),
    fork(tryToDeleteAccountWatcher),
    fork(accountWarningsWatcher),
    fork(watchGetCustomBrandingRequest),
    fork(watchPurchaseExtendedLogsRequest),
    fork(watchHandleValidSenderDomainsToggle),
    fork(watchLoadAccountSuccess),
    fork(watchResetAccountEmail),
];

export default accountSagas;
